import { Box, Button, Chip, Typography, useMediaQuery } from '@mui/material'
import { AddOutlined, ChevronRight } from '@mui/icons-material'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import gaiaTheme from '../../../../root-components/theme'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display'
import SrsReportService from '../../../../shared/services/srs-report/srs-report.service'
import { ISrsReport } from '../../../../shared/services/srs-report/srs-report.model'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { LoadingPage } from '../../loading-page'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'

export const ReportingSrsPage = () => {
  const srsReportService = new SrsReportService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const navigate = useNavigate()
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'))

  const [reports, setReports] = useState<ISrsReport[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [perPage, setPerPage] = useState(100)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    srsReportService.getAll()
      .then((response) => {
        setReports(response)
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setReports([])
        setIsLoading(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error loading your reports, please try again', backgroundColor: 'text.primary' })
      })
  }, [])

  const headCells: IHeadCell[] = [
    { id: 'created', numeric: false, label: 'Created', sortable: false, isHeading: true },
    { id: 'period', numeric: false, label: 'Reporting period', sortable: false },
    { id: 'status', numeric: false, label: 'Status', sortable: false },
    { id: 'generated', numeric: false, label: 'Generated', sortable: false },
    { id: 'action', numeric: false, align: 'right', label: '', sortable: false },
  ]

  // A generated report opens read-only on the detail page; a draft resumes the wizard.
  const openReport = (report: ISrsReport) => {
    navigate(report.status === 'GENERATED'
      ? `/reporting/uk-srs/${report.id}`
      : `/reporting/uk-srs/${report.id}/edit`)
  }

  const handleSelected = (selectedRowIds: string[]) => {
    const report = reports.find((candidate) => candidate.id === selectedRowIds[0])
    if (report) openReport(report)
  }

  const rows = reports.map((report) => ({
    id: report.id,
    created: moment(report.createdAt).format('DD/MM/YYYY'),
    period: report.reportPeriod?.friendlyName || report.reportPeriod?.calendarYear || '—',
    status: (
      <Chip
        size='small'
        label={report.status === 'GENERATED' ? 'Generated' : 'Draft'}
        color={report.status === 'GENERATED' ? 'primary' : 'default'}
        variant={report.status === 'GENERATED' ? 'filled' : 'outlined'}
      />
    ),
    generated: report.generatedAt ? moment(report.generatedAt).format('DD/MM/YYYY') : '—',
    action: (
      <Button
        variant='outlined'
        endIcon={<ChevronRight />}
        // The whole row also opens the report; stop the bubble so one click doesn't navigate twice.
        onClick={(event) => {
          event.stopPropagation()
          openReport(report)
        }}
        sx={{ textTransform: 'none' }}
      >
        {report.status === 'GENERATED' ? 'View' : 'Continue'}
      </Button>
    ),
  }))

  return (
    <PageOuterContainer>
      <PageHeading
        title='Climate Disclosure (UK SRS)'
        includeBackButton={true}
        backButtonLink='/reporting'
        backButtonText='Back to reporting'
        hasTabs={false}
      />
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
        Generate a climate disclosure following the four pillars of UK SRS S2 (IFRS S2): governance,
        strategy, risk management, and metrics and targets. Each report produces a PDF and a web page
        you can publish on your own website, with the emissions figures drawn from your recorded data.
      </Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        The metrics are prepared from your recorded emissions and reconcile with your greenhouse gas
        and SECR reports. Reviewing the content, completing the narrative disclosures and obtaining
        any sign-off your governance requires remain your responsibility, and generating a report
        does not by itself satisfy any reporting obligation.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Button
          variant='contained'
          startIcon={<AddOutlined sx={{ fontSize: 'small' }} />}
          onClick={() => navigate('/reporting/uk-srs/new')}
          sx={{ textTransform: 'none' }}
        >
          <Typography sx={{ pl: 0.5 }}>New report</Typography>
        </Button>
      </Box>

      <LoadingPage pageReady={!isLoading}>
        {rows.length === 0
          ? (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              You have not created a climate disclosure yet.
            </Typography>
          )
          : (
            <TableDisplay
              headCells={headCells}
              rows={rows}
              isMobileMode={isMobileMode}
              handleSelected={handleSelected}
              selected={selected}
              setSelected={setSelected}
              totalRecords={rows.length}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
      </LoadingPage>
    </PageOuterContainer>
  )
}
